// Imports
// *****************************************************
import { Fragment } from 'react';
import { useTranslation } from 'next-i18next';
import { MainMenuInterface } from '@type/header-types';
import useWindowDimensions from '@hooks/useMediaQuery';
import {
  Anchor,
  LinkStyled,
  Dropdown,
  DropdownButton,
  DropdownItems,
  DropdownItem,
} from '@components/common';
import { cn } from '@utils/cn';
import { i18nConfig } from 'i18n-config';
import { clsx } from 'clsx';
import { useDropdownHover } from '@hooks/common/useDropdownHover';
import { isHireRoute } from '@lib/functions/routes/isHireRoute';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { mainMenuLinks } from './links-objects';
import { MainNavbarLink } from './MainNavbarLink';

/**
 * @description Main menu links component. We use the 'role' to determine which links to show and
 * also to check if the user is authenticated (role === undefined means the user is not authenticated).
 * * **role** - user role ('NONE', 'USER', 'COMPANY') - look at mainMenuLinks object for more info
 * * **currentRoute** - current route pathname - needed to highlight the current route in the menu for React pages
 * * **closePopover** - function to close the dropdown popover
 * * **locale** - the current app locale
 */
export const MainMenuLinks = (props: MainMenuInterface) => {
  // Destructuring props
  const {
    currentRoute = '',
    role,
  } = props;

  // Translate
  const { t } = useTranslation('common');

  // filter the main menu links based on the user login state
  let menusToShow = mainMenuLinks;
  if (role === undefined) {
    menusToShow = mainMenuLinks.filter((link) => link.onlyLoggedIn === false || link.onlyLoggedIn === undefined);
  }
  const { isMobile } = useWindowDimensions();

  const isActiveRoute = (linkRoute: string) => {
    if (!currentRoute) {
      return false;
    }

    // Extract the first segment of the path
    const pathSegments = currentRoute.split('/').filter(Boolean); // filters out empty strings

    // Check if the first segment is a supported locale
    const currentLocale = i18nConfig.locales.includes(pathSegments[0] as Locale) ? pathSegments[0] as Locale : null;

    // Get the base path by removing the current locale if it exists
    const basePath = currentLocale ? currentRoute.replace(`/${currentLocale}`, '') : currentRoute;

    return basePath.startsWith(linkRoute);
  };

  const { dropdownRef, buttonRef, closeFuncRef } = useDropdownHover({ enabled: !isMobile });

  const headerInfo = useAppSelector(getUserHeaderInfo);
  const headerInfoExtended = headerInfo as ApiSchema<'HeaderInfo'> & { discountPercentage?: number };


  // Render component
  // - Filter the main menu links based on the user role ('NONE', 'USER', 'COMPANY')
  // *****************************************************
  return (
    <>
      { // Main menu links
        menusToShow
          .filter((item) => (isHireRoute(currentRoute) ? item.forSection?.includes('HIRE') : item.forSection?.includes('TALENT')))
          .map((link) => {
            // destructuring link object
            const {
              type,
              label,
              route: linkRoute,
              onlyMobile,
              icon,
              children,
              dataTestId,
            } = link;

            // If there is selected to show the links only on mobile, return with nothing. We are using isMobile hook.
            if (!isMobile && onlyMobile) { return (<Fragment key={label} />); }

            if (children) {
              return (
                <Dropdown key={label} ref={dropdownRef}>
                  {({ open, close }) => {
                    closeFuncRef.current = close;

                    return (
                      <>
                        <DropdownButton
                          as={MainNavbarLink}
                          type={type}
                          route={linkRoute}
                          label={label}
                          icon={icon}
                          className={cn('relative h-full', open && 'bg-surface-50')}
                          ref={buttonRef}
                          {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
                        />
                        <DropdownItems
                          animation="slideUp"
                          position="left"
                          className="fixed bottom-13 left-0 !z-20 flex w-screen min-w-40 items-start gap-1 bg-surface-50 shadow-none md:absolute md:bottom-auto md:top-full md:w-auto md:flex-col md:pb-2 md:pt-4 md:shadow-md"
                        >
                          {children.map((item) => {
                            if (!isMobile && item.onlyMobile) { return (<Fragment key={label} />); }

                            const Component = item.type === 'react' ? LinkStyled : Anchor;
                            const isSymfony = item.type === 'symfony';
                            const extraProps = isSymfony ? { symfonyRoute: true } : {};
                            const abTestJobAddLink = item.route;

                            return (
                              <DropdownItem
                                key={item.label}
                                as={Component}
                                href={abTestJobAddLink}
                                styling="none"
                                className={clsx(
                                  'w-full text-nowrap px-4 py-3 text-center text-xs outline-none transition hover:bg-surface sm:text-sm lg:py-2 lg:text-left',
                                  !isSymfony && isActiveRoute(abTestJobAddLink) && 'text-secondary',
                                )}
                                {...extraProps}
                                {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
                              >

                                {(item.hasDiscount && headerInfoExtended?.discountPercentage)
                                  ? (
                                    <div className="flex items-center justify-between">
                                      {t(item.label)}
                                      <span
                                        className="ml-1 rounded-md bg-secondary/20 px-1 text-pico font-thin text-secondary"
                                      >
                                        {headerInfoExtended?.discountPercentage}
                                        % OFF
                                      </span>
                                    </div>

                                  ) : t(item.label)}
                              </DropdownItem>
                            );
                          })}
                        </DropdownItems>
                      </>
                    );
                  }}
                </Dropdown>
              );
            }

            return (
              <MainNavbarLink
                key={label}
                type={type}
                route={linkRoute}
                label={label}
                icon={icon}
                {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
              />
            );
          })
      }

    </>
  );
};
